import type { DialogName } from '@/stores/crm-store'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowUpRight } from 'lucide-react'
import { CommandCompanyRow, CommandTableHeader } from '@/components/companies/command-menu/command-table'
import { useCrmStore, useCrmStoreApi } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandFooter,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    Kbd
} from '@/components/ui/command'

const PAGES = [
    { href: '/', label: "Companies" },
    { href: '/deals', label: "Deals Board" },
    { href: '/forecast', label: "Forecast" },
    { href: '/activities', label: "Activities" },
    { href: '/contacts', label: "Contacts" },
    { href: '/sequences', label: "Email Sequences" },
    { href: '/team/strategic-aes', label: "Strategic AEs" },
    { href: '/team/mid-market', label: "Mid Market" },
    { href: '/team/sdr-team', label: "SDR Team" },
    { href: '/reports/q1-forecast', label: "Q1 Forecast" },
    { href: '/reports/slipping-deals', label: "Slipping Deals" },
    { href: '/pipelines/north-america', label: "North America pipeline" },
    { href: '/pipelines/emea-enterprise', label: "EMEA Enterprise pipeline" },
    { href: '/pipelines/apac-expansion', label: "APAC Expansion pipeline" },
    { href: '/help', label: "Help" }
]

const ACTIONS: Array<{ dialog: DialogName, label: string, keywords: string[] }> = [
    { dialog: 'company', label: "New Company", keywords: ["Add", "Create", "Account"] },
    { dialog: 'deal', label: "New Deal", keywords: ["Add", "Create", "Opportunity"] },
    { dialog: 'contact', label: "New Contact", keywords: ["Add", "Create", "Person"] },
    { dialog: 'activity', label: "Log Activity", keywords: ["Call", "Email", "Meeting", "Task", "Note"] },
    { dialog: 'sequence', label: "New Sequence", keywords: ["Email", "Outreach", "Cadence"] },
    { dialog: 'invite', label: "Invite teammates", keywords: ["Share", "Team"] }
]

export default function CommandMenu() {
    const router = useRouter()
    const store = useCrmStoreApi()
    const open = useCrmStore(state => state.searchOpen)
    const setOpen = useCrmStore(state => state.setSearchOpen)
    const companies = useCrmStore(state => state.companies)
    const openDetail = useCrmStore(state => state.openDetail)
    const openDialog = useCrmStore(state => state.openDialog)
    const [query, setQuery] = useState('')
    const actionRan = useRef(false)

    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if (event.key.toLowerCase() !== 'k') return
            if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return

            event.preventDefault()

            const { searchOpen, setSearchOpen } = store.getState()

            setSearchOpen(!searchOpen)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [store])

    function run(action: () => void) {
        actionRan.current = true
        setOpen(false)
        action()
    }

    return (
        <CommandDialog
            open={open}
            onOpenChange={setOpen}
            title="Search"
            description="Search companies, jump to a page or start an action"
            className='max-w-[960px]'
            onCloseAutoFocus={event => {
                if (actionRan.current) event.preventDefault()
                actionRan.current = false
                setQuery('')
            }}
        >
            <Command>
                <CommandInput
                    value={query}
                    onValueChange={setQuery}
                    placeholder="Search companies, pages, actions…"
                    trailing={<Kbd>Esc</Kbd>}
                />
                <CommandTableHeader />
                <CommandList>
                    <CommandEmpty>
                        No results for “{query}”
                    </CommandEmpty>
                    <CommandGroup>
                        {companies.map(company => (
                            <CommandCompanyRow
                                key={company.id}
                                company={company}
                                onSelect={() => run(() => openDetail(company.id))}
                            />
                        ))}
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading="Pages">
                        {PAGES.map(page => (
                            <CommandItem
                                key={page.href}
                                value={`page-${page.href}`}
                                keywords={[page.label, "Go to", "Open"]}
                                onSelect={() => run(() => router.push(page.href))}
                            >
                                <span className='bg-muted flex size-6 shrink-0 items-center justify-center rounded-md shadow-[0px_0px_0px_1px_#232323]'>
                                    <ArrowUpRight aria-hidden className='text-soft size-3' />
                                </span>
                                {page.label}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading="Actions">
                        {ACTIONS.map(action => (
                            <CommandItem
                                key={action.dialog}
                                value={`action-${action.dialog}`}
                                keywords={[action.label, ...action.keywords]}
                                onSelect={() => run(() => openDialog(action.dialog))}
                            >
                                <span className='bg-muted flex size-6 shrink-0 items-center justify-center rounded-md shadow-[0px_0px_0px_1px_#232323]'>
                                    <PlusIcon aria-hidden className='text-soft size-3' />
                                </span>
                                {action.label}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </CommandList>
                <CommandFooter>
                    <span className='flex items-center gap-1.5'>
                        <Kbd>↑</Kbd>
                        <Kbd>↓</Kbd>
                        Navigate
                    </span>
                    <span className='flex items-center gap-1.5'>
                        <Kbd>↵</Kbd>
                        Open
                    </span>
                </CommandFooter>
            </Command>
        </CommandDialog>
    )
}
